"use client";

import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ORANGE_LINK_PACKAGE_IDS } from "@/constants/constants";
import { cleanString } from "@/helpers/cleanString";
import { usePackageDetail } from "@/hooks/usePackageDetail";
import { Link } from "@/i18n/navigation";
import { Coverage } from "@/types/packages/data-only/DataOnlyCountryPackages";
import { Search } from "lucide-react";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";

interface PropsType {
  id: string;
  provider: string;
}

function PackageCoverage({ id, provider }: PropsType) {
  const [searchQuery, setSearchQuery] = useState("");

  const t = useTranslations(`Shared.countryPackages.packageDetailModal`);

  const { data: coverageData, isLoading: isCoverageLoading } =
    usePackageDetail(id);

  const isOrangeProvider = provider === "Orange";
  const showOrangeLink =
    isOrangeProvider && ORANGE_LINK_PACKAGE_IDS.includes(id);

  const error = coverageData?.error;

  const filteredCoverage = useMemo(() => {
    const apiCountries = coverageData?.countries ?? [];
    const apiCoverage = coverageData?.coverage ?? [];

    const coverageByName = new Map<string, Coverage[]>();
    const coverageByCode = new Map<string, Coverage[]>();

    for (const item of apiCoverage) {
      const byName = coverageByName.get(item.country_name);
      if (byName) byName.push(item);
      else coverageByName.set(item.country_name, [item]);

      const byCode = coverageByCode.get(item.code);
      if (byCode) byCode.push(item);
      else coverageByCode.set(item.code, [item]);
    }

    return apiCountries.map((country) => {
      const nameMatches = coverageByName.get(country.name) ?? [];
      const codeMatches = coverageByCode.get(country.code) ?? [];

      let country_coverage: Coverage[];
      if (codeMatches.length === 0) {
        country_coverage = nameMatches;
      } else if (nameMatches.length === 0) {
        country_coverage = codeMatches;
      } else {
        const merged = new Map<number, Coverage>();
        for (const item of nameMatches) merged.set(item.id, item);
        for (const item of codeMatches) merged.set(item.id, item);
        country_coverage = [...merged.values()];
      }

      return {
        country_name: country.name,
        code: country.code,
        code_alpha3: country.code_alpha3,
        local_state_code: country.local_state_code,
        country_coverage,
      };
    });
  }, [coverageData]);

  const filterCoverageByQuery = useMemo(() => {
    if (!searchQuery) return filteredCoverage;

    return filteredCoverage.filter(
      (item) =>
        cleanString(item.country_name).includes(searchQuery) ||
        cleanString(item.code).includes(searchQuery) ||
        cleanString(item.code_alpha3).includes(searchQuery) ||
        cleanString(
          item.local_state_code ? item.local_state_code : "",
        ).includes(searchQuery) ||
        item.country_coverage.some(
          (coverage) =>
            cleanString(coverage.country_name).includes(searchQuery) ||
            cleanString(coverage.network_name).includes(searchQuery) ||
            cleanString(coverage.network_code).includes(searchQuery) ||
            cleanString(coverage.iso).includes(searchQuery),
        ),
    );
  }, [filteredCoverage, searchQuery]);

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid shrink-0 gap-4">
        <p className="text-xl font-500">{t(`networkCoverage.title`)}</p>

        {showOrangeLink && (
          <div className="text-left text-lg text-primary underline underline-offset-4">
            <Link
              href="/orange-global-esim-detail"
              className="hover:text-primary/80"
            >
              View full coverage & plan details
            </Link>
          </div>
        )}

        {!isCoverageLoading && filteredCoverage.length > 2 && (
          <div className="relative h-full w-full shrink-0">
            <Input
              placeholder={t(`networkCoverage.searchPlaceholder`)}
              className="h-[50px] w-full shrink-0 capitalize xl:h-full"
              onChange={(e) => setSearchQuery(cleanString(e.target.value))}
              value={searchQuery}
            />
            <Search className="absolute end-3 top-1/2 -translate-y-1/2 transform text-primary" />
          </div>
        )}
      </div>

      <div className="barMini max-h-[170px] min-h-[170px] overflow-auto rounded-[0.6rem] border bg-secondary px-3 py-2">
        {isCoverageLoading ? (
          <div className="flex flex-col gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div className="flex flex-wrap items-center gap-2" key={index}>
                <Skeleton className="me-6 h-4 w-24 bg-background" />
                <Skeleton className="h-6 w-28 rounded-full bg-background" />
                <Skeleton className="h-6 w-20 rounded-full bg-background" />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {filterCoverageByQuery.length !== 0 ? (
              filterCoverageByQuery.map((item) => (
                <div className="flex flex-wrap gap-1" key={item.code}>
                  <p className="me-6 text-body-md font-500">
                    {item.country_name}
                  </p>
                  {item.country_coverage.map((countryCoverage) => (
                    <div
                      className="flex items-center gap-[0.62rem] rounded-full border bg-background p-1 text-sm"
                      key={countryCoverage.id}
                    >
                      <p>{countryCoverage.network_name}</p>

                      <p className="!rounded-full bg-primary-accent px-2">
                        {countryCoverage.supported_networks_coverages.join(
                          ", ",
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <p
                className={`h-full text-center ${error ? "text-destructive" : "text-muted-foreground"}`}
              >
                {error ? error : t(`networkCoverage.noMatch`)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PackageCoverage;
